import React from 'react';
import { TabPanel } from '../../../../../components/tabs';
import { IClientsTab } from './interfaces';
import { ManagedDataTable } from '../../../../../components/ManagedDataTable';
import { DATA_TABLE_STORAGE_KEYS } from '../../../../../constants';
import { columns } from './helpers';
import { getWarningRowStyling } from '../../../../../helpers/tableStyleHelper';
import {
  ColumnFilters,
  IManagedDataTable,
} from '../../../../../components/ManagedDataTable/interfaces';
import { useGetClientRetentionOverview } from '../../../../../reactQuery/clients';
import {
  ClientRetentionEntry,
  Status,
} from '../../../../../api/clientsApi/interfaces';
import { useLocation, useNavigate } from 'react-router';
import { ROUTES } from '../../../../../routes/Routes';
import { useTranslation } from 'react-i18next';
import { getRowClassName } from '../../helpers';

export const ClientsTab: React.FC<IClientsTab> = ({
  value,
  index,
  groupList,
  agentsList,
  clientsList,
  productsList,
  defaultFilter,
  isAdmin,
}): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const onRowClick = (row: ClientRetentionEntry): void => {
    navigate(ROUTES.clients.details(row.id), { state: { from: location } });
  };

  const columnFilters: ColumnFilters = [
    {
      column: 'lastContactDate',
      label: t('clients:relationshipManagement.columns.lastContactDate'),
      type: 'date',
    },
    {
      column: 'dueDate',
      label: t('clients:relationshipManagement.columns.dueDate'),
      type: 'date',
    },
    {
      column: 'name',
      filterName: 'clients',
      label: t('entities:activityLog.clients'),
      type: 'multivalue',
      options: clientsList || [],
    },
    {
      column: 'group',
      label: t('entities:client.group'),
      type: 'multivalue',
      options: groupList || [],
    },
    {
      column: 'status',
      label: t('entities:client.status'),
      type: 'multivalue',
      options: Object.values(Status).map((statusKey) => ({
        id: statusKey,
        name: t(`entities:client.enums.status.${statusKey}`),
      })),
    },
    {
      column: 'contractedProducts',
      label: t('entities:client.contractedProducts'),
      type: 'multivalue',
      options:
        productsList?.map((product) => {
          return {
            id: product.id,
            name: product.name,
          };
        }) || [],
    },
    {
      column: 'potentialProducts',
      label: t('entities:client.potentialProducts'),
      type: 'multivalue',
      options:
        productsList?.map((product) => {
          return {
            id: product.id,
            name: product.name,
          };
        }) || [],
    },
  ];

  if (isAdmin) {
    columnFilters.push({
      column: 'responsibleAgents',
      label: t('entities:client.responsibleAgents'),
      type: 'multivalue',
      options:
        agentsList?.map((agent) => {
          return {
            id: agent.id,
            name: `${agent.firstName} ${agent.lastName}`,
          };
        }) || [],
    });
  }

  return (
    <TabPanel value={value} index={index}>
      <ManagedDataTable
        query={
          useGetClientRetentionOverview as IManagedDataTable<ClientRetentionEntry>['query']
        }
        columns={columns}
        filter={defaultFilter}
        onRowClick={onRowClick}
        storageKey={DATA_TABLE_STORAGE_KEYS.clientRetentionOverview}
        initialSort={{ field: 'daysLeft', sort: 'asc' }}
        getRowClassName={getRowClassName}
        sx={getWarningRowStyling()}
        columnFilters={columnFilters}
      />
    </TabPanel>
  );
};
